<template>
  <v-dialog v-model="dialogModel" max-width="700" persistent>
    <v-card class="rounded-xl">
      <v-card-title class="d-flex align-center pb-0">
        <v-icon color="#198C19" class="mr-2">mdi-check-circle-outline</v-icon>
        <span class="dialog-title">อนุมัติไรเดอร์</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="mx-4 mt-2" />

      <v-card-text class="pt-4">
        <div v-if="rider" class="rider-info mb-4">
          <div class="d-flex align-center mb-2">
            <v-icon small class="mr-2">mdi-account</v-icon>
            <span class="font-weight-medium">{{ riderDisplayName }}</span>
          </div>
          <div class="d-flex align-center mb-2">
            <v-icon small class="mr-2">mdi-email-outline</v-icon>
            <span>{{ rider.email || '-' }}</span>
          </div>
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-phone-outline</v-icon>
            <span>{{ rider.phone || '-' }}</span>
          </div>
        </div>

        <v-divider class="mb-4" />

        <div class="section-title mb-3">กำหนดบริการสำหรับรถแต่ละคัน</div>

        <div v-if="loadingServiceTypes" class="d-flex justify-center py-4">
          <v-progress-circular indeterminate color="#F4811F" />
        </div>

        <div v-else-if="vehicleList.length === 0" class="text-center py-4">
          <span class="grey--text">ไม่พบข้อมูลรถของไรเดอร์</span>
        </div>

        <div v-else>
          <v-card
            v-for="(vehicle, idx) in vehicleList"
            :key="vehicle.id"
            outlined
            class="pa-4 mb-3 rounded-lg"
          >
            <div class="d-flex align-center mb-3">
              <v-icon color="#F4811F" class="mr-2">mdi-car</v-icon>
              <span class="font-weight-bold">รถคันที่ {{ idx + 1 }}</span>
              <v-chip small class="ml-2" color="#F3F4F6">{{ vehicle.vehicleType }}</v-chip>
              <span v-if="vehicle.vehiclePlate" class="ml-2 grey--text text--darken-1">
                {{ vehicle.vehiclePlate }}
              </span>
            </div>

            <div class="d-flex flex-wrap" style="gap: 8px">
              <v-checkbox
                v-for="st in serviceTypes"
                :key="st.id"
                v-model="selectedServices[vehicle.id]"
                :value="st.id"
                :label="st.name"
                hide-details
                dense
                class="mt-0 mr-4"
                color="#F4811F"
              />
            </div>

            <div
              v-if="submitted && (!selectedServices[vehicle.id] || selectedServices[vehicle.id].length === 0)"
              class="error--text text-caption mt-2"
            >
              กรุณาเลือกอย่างน้อย 1 บริการ
            </div>
          </v-card>
        </div>
      </v-card-text>

      <v-divider class="mx-4" />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text class="rounded-lg" @click="close">ยกเลิก</v-btn>
        <v-btn
          color="#198C19"
          dark
          class="rounded-lg elevation-0 px-6"
          :loading="saving"
          @click="confirm"
        >
          <v-icon left small>mdi-check</v-icon>
          ยืนยันอนุมัติ
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getRiderServiceTypesApi } from "@/services/api/adminServices";

export default {
  name: "ApproveDialog",

  props: {
    value: Boolean,
    rider: { type: Object, default: null },
    saving: Boolean,
  },

  data() {
    return {
      serviceTypes: [],
      selectedServices: {},
      loadingServiceTypes: false,
      submitted: false,
    };
  },

  computed: {
    dialogModel: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },

    vehicleList() {
      return (this.rider && this.rider.vehicles) || [];
    },

    riderDisplayName() {
      if (!this.rider) return "-";
      const p = this.rider.natId_prefix || "";
      const f = this.rider.natId_firstName || "";
      const l = this.rider.natId_lastName || "";
      const parts = [p, f, l].filter(Boolean);
      return parts.length ? parts.join(" ") : this.rider.email || "-";
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.submitted = false;
        this.initSelectedServices();
        this.loadServiceTypes();
      }
    },
  },

  methods: {
    initSelectedServices() {
      const map = {};
      for (const v of this.vehicleList) {
        const existing = (v.services || []).map((s) => s.serviceTypeId);
        map[v.id] = existing.length ? [...existing] : [];
      }
      this.selectedServices = map;
    },

    async loadServiceTypes() {
      this.loadingServiceTypes = true;
      try {
        const res = await getRiderServiceTypesApi();
        this.serviceTypes = (res && res.data) || res || [];
      } catch (e) {
        console.error("loadServiceTypes error", e);
        this.serviceTypes = [];
      } finally {
        this.loadingServiceTypes = false;
      }
    },

    confirm() {
      this.submitted = true;

      const hasEmpty = this.vehicleList.some(
        (v) => !this.selectedServices[v.id] || this.selectedServices[v.id].length === 0
      );
      if (hasEmpty) return;

      const vehicleServices = this.vehicleList.map((v) => ({
        vehicleId: v.id,
        serviceTypeIds: this.selectedServices[v.id] || [],
      }));

      this.$emit("confirm", { vehicleServices });
    },

    close() {
      this.dialogModel = false;
    },
  },
};
</script>

<style scoped>
.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2a39;
}
.rider-info {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px 16px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2a39;
}
</style>
